import React from "react";
import { diatonicIndex, parsePitch } from "@/lib/music";
import { playPitch } from "@/lib/audio";
import { beamGroups, fitBeam, groupUnit, groupUp } from "@/lib/beams";

const LEN = { whole: 4, half: 2, quarter: 1, eighth: 0.5, "16th": 0.25 };
const REST_LEN = { semibreve: 4, minim: 2, crotchet: 1, quaver: 0.5 };

// Staff geometry
const LINE_YS = [40, 52, 64, 76, 88];
const STEP = 6; // px per diatonic half-step (line->space)
const TOP_Y = 40;

// Reference: top staff line
const REF = {
  treble: { diatonic: 5 * 7 + 3, y: TOP_Y }, // F5
  bass: { diatonic: 3 * 7 + 5, y: TOP_Y }, // A3
  percussion: { diatonic: 5 * 7 + 3, y: TOP_Y }, // same layout as treble
  none: { diatonic: 5 * 7 + 3, y: TOP_Y },
  alto: { diatonic: 4 * 7 + 4, y: TOP_Y }, // G4
  tenor: { diatonic: 4 * 7 + 2, y: TOP_Y }, // E4
};

// Noto Music: treble at 4 spaces with baseline on the bottom line wraps the G (2nd) line;
// bass at 4.55 spaces with baseline 0.645em below the F (4th) line puts the dot on F and the colon either side.
const CLEF_POS = { treble: { size: 48, y: 88 }, bass: { size: 55, y: 52 + 0.645 * 55 }, alto: { size: 47, y: 64 + 24 }, tenor: { size: 47, y: 52 + 24 } };
const CLEF_GLYPH = { treble: "\u{1D11E}", bass: "\u{1D122}", alto: "\u{1D121}", tenor: "\u{1D121}" };

// Cymbals in drum notation (hi-hat / ride / crash) always use an X notehead.
const CYMBAL_PITCHES = new Set(["G5", "A5"]);

// Resolve the notehead style for a single pitch inside a note.
// Priority: explicit per-pitch `heads[k]` -> group-level `head` -> auto X for
// cymbal pitches in percussion clef -> normal ellipse.
function headStyle(n, pitch, k, clef) {
  if (n.heads && n.heads[k] !== undefined && n.heads[k] !== null) return n.heads[k];
  if (n.head) return n.head;
  if (clef === "percussion" && CYMBAL_PITCHES.has(pitch)) return "x";
  return "normal";
}

function noteY(pitch, clef) {
  const d = diatonicIndex(pitch);
  const ref = REF[clef];
  return ref.y + (ref.diatonic - d) * STEP;
}

const noteYs = (n, clef) => (n.y != null ? [n.y] : (n.pitches || [n.pitch]).map((p) => noteY(p, clef)));

// Beam quavers by beat (half-bar in 4/4): returns { stems: {i: {up, end}}, beams: [{x0, y0, x1, y1, up}] }.
function beamLayout(notes, clef, time, xAt) {
  let pos = 0;
  const items = notes.map((n) => {
    if (n.barline) { pos = 0; return { beam: false, pos: 0, len: 0 }; }
    const len = n.rest ? REST_LEN[n.rest] ?? 1 : (LEN[n.duration] ?? 1) * (n.dot ? 1.5 : 1) * (n.tuplet ? 2 / 3 : 1);
    const it = { pos, len, beam: !n.rest && (n.duration === "eighth" || n.duration === "16th") && !n.dot && !n.noBeam };
    pos += len;
    return it;
  });
  const stems = {}, beams = [];
  beamGroups(items, groupUnit(time)).forEach((g) => {
    const geo = g.map((i) => { const ys = noteYs(notes[i], clef); return { top: Math.min(...ys), bottom: Math.max(...ys) }; });
    const first = notes[g[0]];
    const up = first.stemDir ? first.stemDir === "up" : groupUp(geo, 64);
    const pts = g.map((i, k) => ({ ...geo[k], x: xAt(i) + (up ? 7 : -7) }));
    const line = fitBeam(pts, up);
    g.forEach((i, k) => { stems[i] = { up, end: line.at(pts[k].x) }; });
    const a = pts[0].x, b = pts[pts.length - 1].x;
    const second = [];
    g.slice(1).forEach((i, k) => { if (notes[i].duration === "16th" && notes[g[k]].duration === "16th") second.push([pts[k].x, pts[k + 1].x]); });
    beams.push({ x0: a, y0: line.at(a), x1: b, y1: line.at(b), up, second, at: line.at, tuplet: g.every((i) => notes[i].tuplet) && notes[g[0]].tuplet });
  });
  return { stems, beams };
}

function ledgerLines(yy) {
  const lines = [];
  if (yy < TOP_Y) {
    for (let ly = 28; ly >= yy; ly -= 12) lines.push(ly);
  } else if (yy > LINE_YS[4]) {
    for (let ly = 100; ly <= yy; ly += 12) lines.push(ly);
  }
  return lines;
}

const letterName = (p) => p[0].toUpperCase() + p.slice(1).replace(/-?\d+$/, "").replace(/#/g, "\u266F").replace(/b/g, "\u266D");

export const StaffViewer = ({
  clef = "treble",
  notes = [],
  width = 520,
  onNoteClick,
  playOnClick = true,
  showLabels = false,
  testId = "staff",
  time,
  meter,
  activeIndex = -1,
  onGapClick,
  showLetters = false,
  lineNumbers = false,
  tabFrets,
  tabLines = 6,
}) => {
  const [flash, setFlash] = React.useState(-1);
  const letters = showLetters && ["treble", "bass", "alto", "tenor"].includes(clef);
  const lowest = Math.max(0, ...notes.filter((x) => !x.barline && !x.rest).map((x) => (x.y != null ? x.y : Math.max(...(x.pitches || [x.pitch]).map((p) => noteY(p, clef))))));
  const LY = Math.max(125, lowest + 22);
  const tabTop = LY + 34;
  const height = tabFrets ? tabTop + (tabLines - 1) * TAB_GAP + 22 : notes.length ? Math.max(150, LY + 25) : 110;
  const startX = time ? 116 : clef === "none" ? 60 : 92;
  const spacing = notes.length > 1 ? Math.min(78, (width - startX - 30) / notes.length) : 0;
  const highest = Math.min(40, ...notes.filter((x) => !x.barline && !x.rest).map((x) => (x.y != null ? x.y : Math.min(...(x.pitches || [x.pitch]).map((p) => noteY(p, clef))))));
  const symTop = Math.min(notes.some((x) => x.symbol) ? -28 : 0, Math.floor(highest - 14)) - (notes.some((x) => x.symbol) && highest < 30 ? 22 : 0);
  const beamed = beamLayout(notes, clef, meter || time, (i) => startX + i * spacing);

  const handleClick = (n, i) => {
    setFlash(i);
    setTimeout(() => setFlash((f) => (f === i ? -1 : f)), 600);
    const ps = n.pitches || [n.pitch];
    if (playOnClick) ps.forEach((p) => playPitch(p, n.duration === "whole" ? 1.1 : 0.7));
    if (onNoteClick) onNoteClick(n);
  };

  return (
    <svg
      viewBox={`0 ${symTop} ${width} ${height - symTop}`}
      className="staff-svg w-full max-w-full"
      data-testid={testId}
      role="group"
      aria-label={`${clef === "none" ? "Stave with no clef" : `${clef === "alto" || clef === "tenor" ? `C (${clef})` : clef} clef stave`}${notes.length ? `: ${notes.filter((x) => !x.barline).map((x) => x.rest ? `${x.rest} rest` : (x.pitches || [x.pitch]).join(" and ").replace(/#/g, " sharp").replace(/b(?=\d)/g, " flat")).join(", ")}` : ""}`}
    >
      {/* staff lines */}
      {LINE_YS.map((y) => (
        <line key={y} x1={20} y1={y} x2={width - 20} y2={y} stroke="var(--staff-line)" strokeWidth="1.2" />
      ))}
      {lineNumbers && LINE_YS.map((y, k) => (
        <text key={`n${y}`} x={11} y={y + 4} fontSize="12" fontWeight="700" textAnchor="middle" fill="#F59E0B" fontFamily="monospace" data-testid={`staff-line-number-${5 - k}`}>{5 - k}</text>
      ))}
      {notes.map((n, i) => n.symbol && (
        <text key={`sym${i}`} x={startX + i * spacing} y={symTop + 18} fontSize="15" fontWeight="700" textAnchor="middle" fill="var(--staff-label)" fontFamily="serif" data-testid={`${testId}-symbol-${i}`}>{n.symbol}</text>
      ))}
      {/* clef */}
      {clef === "none" ? null : CLEF_GLYPH[clef] ? (
        <text x={28} y={CLEF_POS[clef].y} fontSize={CLEF_POS[clef].size} fill="var(--staff-note)" fontFamily="'Noto Music', serif" data-testid={`clef-glyph-${clef}`}>
          {CLEF_GLYPH[clef]}
        </text>
      ) : (
        <g>
          <rect x={34} y={52} width={5} height={24} fill="var(--staff-note)" />
          <rect x={43} y={52} width={5} height={24} fill="var(--staff-note)" />
        </g>
      )}

      {time && time.split("/").map((t, k) => (
        <text key={k} x={84} y={k ? 87 : 63} fontSize="24" fontWeight="700" textAnchor="middle" fill="var(--staff-note)" fontFamily="serif">{t}</text>
      ))}
      {notes.map((n, i) => {
        const cx = startX + i * spacing;
        if (n.barline) {
          const bx = cx - spacing / 2 + 10;
          return n.barline === "double" ? (
            <g key={i}><line x1={bx - 5} y1={40} x2={bx - 5} y2={88} stroke="var(--staff-line)" strokeWidth="1.4" /><line x1={bx} y1={40} x2={bx} y2={88} stroke="var(--staff-note)" strokeWidth="4" /></g>
          ) : <line key={i} x1={bx} y1={40} x2={bx} y2={88} stroke="var(--staff-line)" strokeWidth="1.4" />;
        }
        if (n.rest) {
          return (
            <g key={i} data-testid={`staff-rest-${n.rest}-${i}`}>
              {n.rest === "crotchet" && (
                <path d={`M${cx - 4} 45 l8 10 q-6 5 -1 12 l4 6 q-7 -4 -10 1 q-2 4 3 8`} fill="none" stroke="var(--staff-note)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              )}
              {n.rest === "quaver" && (
                <g fill="var(--staff-note)" stroke="var(--staff-note)">
                  <circle cx={cx - 4} cy={59} r={3.4} stroke="none" />
                  <path d={`M${cx - 4} 61 q5 2 9 -4 l-7 22`} fill="none" strokeWidth="2.2" strokeLinecap="round" />
                </g>
              )}
              {(n.rest === "minim" || n.rest === "semibreve") && (
                <>
                  {n.hint && <line x1={cx - 20} y1={n.rest === "minim" ? 64 : 52} x2={cx + 20} y2={n.rest === "minim" ? 64 : 52} stroke="#38BDF8" strokeWidth="3" />}
                  <rect x={cx - 11} y={n.rest === "minim" ? 57 : 52} width={22} height={7} fill="var(--staff-note)" />
                  {n.hint && <text x={cx} y={n.rest === "minim" ? 30 : 14} fontSize="11" fontWeight="700" textAnchor="middle" fill="#38BDF8" fontFamily="sans-serif">{n.rest === "minim" ? "sits on line 3" : "hangs off line 4"}</text>}
                </>
              )}
              {n.label && <text x={cx} y={LY} fontSize="13" textAnchor="middle" fill="var(--staff-label)" fontFamily="monospace">{n.label}</text>}
            </g>
          );
        }
        let tieTo = null;
        if (n.tie) { for (let j = i + 1; j < notes.length; j++) if (!notes[j].barline) { tieTo = startX + j * spacing; break; } }
        const ps = n.pitches || [n.pitch];
        const lit = i === activeIndex || i === flash;
        const noteColor = lit ? "#EF4444" : n.color;
        const filled = n.duration === "quarter" || n.duration === "eighth" || n.duration === "16th" || !n.duration;
        const hasStem = n.duration !== "whole";
        const ys = noteYs(n, clef);
        const topY = Math.min(...ys);
        const bottomY = Math.max(...ys);
        const bm = beamed.stems[i];
        const stemUp = bm ? bm.up : n.stemDir ? n.stemDir === "up" : bottomY > 64;
        return (
          <g
            key={i}
            className="staff-note"
            data-testid={n.testId || `staff-note-${ps.join("-")}`}
            onClick={() => handleClick(n, i)}
            style={{ cursor: "pointer" }}
            role="button"
            tabIndex={0}
            aria-label={`Play ${ps.join(" and ")}${n.label ? `, ${n.label}` : ""}`}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(n, i); } }}
          >
            {ps.map((p, k) => {
              const yy = ys[k];
              const pp = parsePitch(p);
              const acc = pp && pp.accidental;
              return (
                <g key={k}>
                  {(n.ledgerAt || ledgerLines(yy)).map((ly) => (
                    <line key={ly} x1={cx - 13} y1={ly} x2={cx + 13} y2={ly} stroke="var(--staff-line)" strokeWidth="1.2" />
                  ))}
                  {n.natural && !acc && (
                    <text x={cx - 24} y={yy + 6} fontSize="22" fill={noteColor || "var(--staff-note)"} fontFamily="serif">{"\u266E"}</text>
                  )}
                  {acc && (
                    <text x={cx - 26} y={yy + 5} fontSize="22" fill={noteColor || "var(--staff-note)"} fontFamily="serif">
                      {acc === "#" ? "\u266F" : "\u266D"}
                    </text>
                  )}
                  {n.dot && <circle cx={cx + 13} cy={yy - 2} r="2.4" fill={noteColor || "var(--staff-note)"} />}
                  {tieTo && k === 0 && <path d={`M ${cx + 4} ${yy + 9} Q ${(cx + tieTo) / 2} ${yy + 24} ${tieTo - 4} ${yy + 9}`} fill="none" stroke="var(--staff-note)" strokeWidth="1.6" />}
                  {headStyle(n, p, k, clef) === "x" ? (
                    <g stroke={noteColor || "var(--staff-note)"} strokeWidth="1.8" strokeLinecap="round">
                      <line x1={cx - 6} y1={yy - 6} x2={cx + 6} y2={yy + 6} />
                      <line x1={cx - 6} y1={yy + 6} x2={cx + 6} y2={yy - 6} />
                    </g>
                  ) : (
                    <ellipse
                      cx={cx}
                      cy={yy}
                      rx="7.5"
                      ry="5.5"
                      transform={`rotate(-20 ${cx} ${yy})`}
                      fill={filled ? noteColor || "var(--staff-note)" : "var(--staff-hollow)"}
                      stroke={noteColor || "var(--staff-note)"}
                      strokeWidth="1.6"
                    />
                  )}
                </g>
              );
            })}
            {hasStem && n.stem === "both" && (
              <line x1={cx + 7} y1={bottomY - 1} x2={cx + 7} y2={topY - 34} stroke={noteColor || "var(--staff-note)"} strokeWidth="1.6" />
            )}
            {hasStem &&
              (stemUp ? (
                <line x1={cx + 7} y1={bottomY - 1} x2={cx + 7} y2={bm ? bm.end : topY - 34} stroke={noteColor || "var(--staff-note)"} strokeWidth="1.6" />
              ) : (
                <line x1={cx - 7} y1={topY + 1} x2={cx - 7} y2={bm ? bm.end : bottomY + 34} stroke={noteColor || "var(--staff-note)"} strokeWidth="1.6" />
              ))}
            {hasStem && !bm && (n.duration === "eighth" || n.duration === "16th") && (n.duration === "16th" ? [0, 8] : [0]).map((o) => (stemUp
              ? <path key={o} d={`M ${cx + 7} ${topY - 34 + o} q 4 10 10 16`} fill="none" stroke={noteColor || "var(--staff-note)"} strokeWidth="1.8" />
              : <path key={o} d={`M ${cx - 7} ${bottomY + 34 - o} q 6 -8 10 -18`} fill="none" stroke={noteColor || "var(--staff-note)"} strokeWidth="1.8" />))}
            {letters ? (
              <>
                <text x={cx} y={LY} fontSize="14" fontWeight="700" textAnchor="middle" fill={lit ? "#EF4444" : "var(--staff-label)"} fontFamily="monospace" data-testid={`staff-letter-${i}`}>
                  {ps.map(letterName).join("+")}
                </text>
                {n.label && n.label !== ps.map(letterName).join("+") && (
                  <text x={cx} y={LY + 16} fontSize="10" textAnchor="middle" fill="var(--staff-label)" fontFamily="monospace">{n.label}</text>
                )}
              </>
            ) : (showLabels || n.label) && n.label !== "" && (
              <text x={cx} y={LY} fontSize="13" textAnchor="middle" fill="var(--staff-label)" fontFamily="monospace">
                {n.label || ps.join("+")}
              </text>
            )}
          </g>
        );
      })}
      {beamed.beams.map((b, k) => {
        const t = b.up ? 5 : -5;
        const o = b.up ? 8 : -8;
        return (
          <g key={`beam-${k}`}>
            <polygon points={`${b.x0 - 0.8},${b.y0} ${b.x1 + 0.8},${b.y1} ${b.x1 + 0.8},${b.y1 + t} ${b.x0 - 0.8},${b.y0 + t}`} fill="var(--staff-note)" data-testid={`${testId}-beam-${k}`} />
            {b.second.map(([p, q]) => <polygon key={p} points={`${p - 0.8},${b.at(p) + o} ${q + 0.8},${b.at(q) + o} ${q + 0.8},${b.at(q) + o + t} ${p - 0.8},${b.at(p) + o + t}`} fill="var(--staff-note)" />)}
            {b.tuplet && <text x={(b.x0 + b.x1) / 2} y={b.up ? Math.min(b.y0, b.y1) - 5 : Math.max(b.y0, b.y1) + 15} fontSize="13" fontStyle="italic" fontWeight="700" textAnchor="middle" fill="var(--staff-note)" fontFamily="serif" data-testid={`${testId}-tuplet-${k}`}>{b.tuplet}</text>}
          </g>
        );
      })}
      {onGapClick && notes.slice(1).map((n, k) => (n.barline || notes[k].barline ? null : (
        <rect key={`gap-${k}`} x={startX + k * spacing + 9} y={34} width={Math.max(6, spacing - 18)} height={60} fill="transparent" style={{ cursor: "pointer" }}
          onClick={() => onGapClick(k)} data-testid={`${testId}-gap-${k}`} />
      )))}
      {tabFrets && <TabRows tabFrets={tabFrets} lines={tabLines} top={tabTop} width={width} xAt={(i) => startX + i * spacing} testId={`${testId}-tab`} />}
    </svg>
  );
};

const TAB_GAP = 13;
// Guitar TAB drawn under the stave, each fret number lined up under its note (rows: 0 = high e ... 5 = low E).
const TabRows = ({ tabFrets, lines = 6, top, width, xAt, testId }) => (
  <g data-testid={testId}>
    {[...Array(lines).keys()].map((r) => <line key={r} x1={20} y1={top + r * TAB_GAP} x2={width - 20} y2={top + r * TAB_GAP} stroke="var(--staff-line)" strokeWidth="1" />)}
    {["T", "A", "B"].map((ch, k) => <text key={ch} x={34} y={top + (lines === 6 ? 22 : 12) + k * (lines === 6 ? 16 : 13)} fontSize={lines === 6 ? 14 : 12} fontWeight="700" textAnchor="middle" fill="var(--staff-note)" fontFamily="monospace">{ch}</text>)}
    {tabFrets.map((col, i) => col && Object.entries(col).map(([r, f]) => (
      <g key={`${i}-${r}`}>
        <rect x={xAt(i) - 8} y={top + r * TAB_GAP - 7} width={16} height={14} fill="hsl(var(--card))" />
        <text x={xAt(i)} y={top + r * TAB_GAP + 4.5} fontSize="12" fontWeight="700" textAnchor="middle" fill="#F59E0B" fontFamily="monospace" data-testid={`${testId}-${i}-${r}`}>{f}</text>
      </g>
    )))}
  </g>
);

export default StaffViewer;
